"use client";
import { useState, useMemo } from "react";
import { useTranslations } from "@zapier/i18n";

import {
  Avatar,
  Button,
  Key,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Spinner,
  Text,
} from "@zapier/design-system";

import { FilterTypeahead } from "../FilterTypeahead";
import styles from "./ChangeOwnerModal.module.css";
import { LinkedAssetsWarning } from "../LinkedAssetsWarning";
import { AssetTypes } from "@zapier/utils";
import { LinkedAsset } from "../LinkedAssetsWarning/LinkedAssetsWarning";
import { Member } from "@zapier/client";

export interface ChangeOwnerUser {
  id: string | number;
  name: string;
  email?: string | null;
  photoUrl?: string | null;
  isDisabled?: boolean;
}

interface Props {
  id: string | number;
  title: string;
  header?: string;
  currentOwnerId?: number;
  bodyText?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  linkedAssets?: LinkedAsset[];
  users: ChangeOwnerUser[];
  assetType: AssetTypes;
  isLoading?: boolean;
  onSearch?: (search: string) => void;
  isSubmitting?: boolean;
  isUsersLoading?: boolean;
  onSubmit?: (customuserId: string) => void;
  preselectedUser?: Member;
}

function ChangeOwnerContent({
  title,
  header,
  currentOwnerId,
  bodyText,
  onSubmit,
  setIsOpen,
  linkedAssets,
  users,
  assetType,
  isLoading,
  isUsersLoading,
  onSearch,
  isSubmitting,
  preselectedUser,
}: Props) {
  const [_searchValue, setSearchValue] = useState<string>("");
  const [selectedKey, setSelectedKey] = useState<Key>(
    preselectedUser?.id || currentOwnerId || "",
  );
  const t = useTranslations("ChangeOwnerModal");

  // callback fired when a key is selected from the dropdown
  const selectKey = (key: Key | null) => {
    setSelectedKey(key || "");
  };

  const searchChange = (search: string) => {
    setSearchValue(search);
    onSearch?.(search);
  };

  const isValid = preselectedUser
    ? !!preselectedUser.id &&
      String(preselectedUser.id) !== String(currentOwnerId)
    : !!selectedKey && String(selectedKey) !== String(currentOwnerId);

  const submit = () => {
    if (!isValid) {
      return;
    }
    onSubmit?.(String(preselectedUser?.id || selectedKey));
  };

  const computedBodyText = useMemo(() => {
    if (preselectedUser) {
      const userEmail = preselectedUser.email
        ? ` (${preselectedUser.email})`
        : "";
      return t("body_text_with_user", {
        assetType,
        user: `${preselectedUser.fullName}${userEmail}`,
      });
    }

    return (
      bodyText ||
      t("body_text", {
        assetType: assetType === "interface" ? "form" : assetType,
      })
    );
  }, [bodyText, preselectedUser, assetType]);

  return (
    <ModalDialogRoot isOpen={true} onOpenChange={setIsOpen}>
      {isLoading ? (
        <>
          <ModalDialogHeader textAlign="start">
            {header ?? t("header", { title })}
          </ModalDialogHeader>
          <div className={styles.loadingContainer}>
            <Spinner />
          </div>
        </>
      ) : (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the form deliberately stops click/keydown propagation to keep the modal open and submit on Enter
        <form
          onSubmit={submit}
          onClick={(event) => event.stopPropagation()} // prevent the modal from closing when clicking inside the form
          onKeyDown={
            // if the enter key is being pressed submit the form
            (event) => {
              if (!isValid) return;
              return event.key === "Enter" && submit();
            }
          }
        >
          <ModalDialogHeader textAlign="start">
            {header ?? t("header", { title })}
          </ModalDialogHeader>

          <ModalDialogBody textAlign="start">
            <div className={styles.changeOwnerModal}>
              <Text>{computedBodyText}</Text>
              {!!linkedAssets?.length && (
                <LinkedAssetsWarning linkedAssets={linkedAssets} />
              )}
              {!preselectedUser && (
                <FilterTypeahead
                  items={users}
                  label={t("new_owner_label")}
                  isRequired={true}
                  onSearchChange={searchChange}
                  onSelectKey={selectKey}
                  placeholder={t("new_owner_placeholder")}
                  selectedKey={selectedKey}
                  isBusy={isUsersLoading}
                  showNoResults={true}
                  renderItem={(item: ChangeOwnerUser) => {
                    return (
                      <div className={styles.menuItem}>
                        <Avatar url={item.photoUrl} name={item.name} />
                        <Text tag="p">
                          {item.name || ""}{" "}
                          {item.email ? `(${item.email})` : ""}{" "}
                        </Text>
                      </div>
                    );
                  }}
                />
              )}
            </div>
          </ModalDialogBody>

          <ModalDialogFooter>
            <Button variant="secondary" onPress={() => setIsOpen(false)}>
              {t("cancel")}
            </Button>
            <Button
              isDisabled={!isValid || isLoading || isSubmitting}
              onPress={submit}
              type="submit"
            >
              {t("submit")}
            </Button>
          </ModalDialogFooter>
        </form>
      )}
    </ModalDialogRoot>
  );
}

export default function ChangeOwner({
  id,
  title,
  header,
  currentOwnerId,
  bodyText,
  isOpen,
  setIsOpen,
  linkedAssets,
  users,
  assetType,
  isLoading,
  onSearch,
  isUsersLoading,
  onSubmit,
  isSubmitting,
  preselectedUser,
}: Props) {
  if (!isOpen) {
    return;
  }

  /** This callback needs to happen outside of the modal so that it can
   * still display a notification when the request completes after the modal has closed.
   */
  const handleChangeOwner = (customuserId: string) => {
    onSubmit?.(customuserId);
    setIsOpen(false);
  };

  return (
    <ChangeOwnerContent
      id={id}
      title={title}
      header={header}
      currentOwnerId={currentOwnerId}
      bodyText={bodyText}
      isOpen={isOpen}
      onSubmit={handleChangeOwner}
      setIsOpen={setIsOpen}
      linkedAssets={linkedAssets}
      users={users}
      assetType={assetType}
      isLoading={isLoading}
      onSearch={onSearch}
      isUsersLoading={isUsersLoading}
      isSubmitting={isSubmitting}
      preselectedUser={preselectedUser}
    />
  );
}
